import React, { createContext, useContext, useReducer, useCallback, useEffect, useRef } from 'react';

/* ── State shape ── */
const initialState = {
  conversation: {
    messages: [],          // { id, type, content, sender, timestamp, cardData? }
    isListening: false,
    isProcessing: false,
    inputMode: 'voice',    // 'voice' | 'text'
  },
  document: {
    title: '',
    sections: {
      job_title:        { content: '', status: 'empty' },
      about_company:    { content: '', status: 'empty' },
      job_description:  { content: '', status: 'empty' },
      requirements:     { content: '', status: 'empty' },
      benefits:         { content: '', status: 'empty' },
      conditions:       { content: '', status: 'empty' },
    },
    completionPercentage: 0,
    // Safety net for concept-popup trigger: only fires when user has explicitly
    // confirmed concept-generation, OR when the AI batch-fills 4+ sections in
    // a single response (the concept-generation moment). Prevents prompt-drift
    // from popping the completion screen prematurely. Set via SET_CONCEPT_CONFIRMED.
    userConfirmedConcept: false,
  },
  ui: {
    activePanel: 'conversation', // for tablet/mobile
    isMobile: false,
    documentSheetOpen: false,
    started: false,
    hasStarted: false,
  },
  session: {
    sessionId: null,
    startedAt: null,
  },
};

/* ── Session persistence ── */
const STORAGE_KEY = 'hai-workspace-session';
const SESSION_MAX_AGE = 24 * 60 * 60 * 1000; // 24 hours

function saveSession(state) {
  try {
    const data = {
      document: state.document,
      session: state.session,
      messages: state.conversation.messages.slice(-30), // keep last 30 messages
      savedAt: Date.now(),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch { /* quota exceeded or private mode, ignore */ }
}

export function loadSavedSession() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    // Expire after 24 hours
    if (Date.now() - data.savedAt > SESSION_MAX_AGE) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    // Only restore if there's actual content (at least one filled section or messages)
    const hasContent = Object.values(data.document?.sections || {}).some(s => s.status !== 'empty');
    const hasMessages = data.messages?.length > 0;
    if (!hasContent && !hasMessages) return null;
    return data;
  } catch {
    return null;
  }
}

export function clearSavedSession() {
  localStorage.removeItem(STORAGE_KEY);
}

/* ── Section order for completion calculation ── */
const SECTION_KEYS = [
  'job_title', 'about_company', 'job_description',
  'requirements', 'benefits', 'conditions',
];

function calcCompletion(sections) {
  const filled = SECTION_KEYS.filter(k => sections[k].status !== 'empty').length;
  return Math.round((filled / SECTION_KEYS.length) * 100);
}

/* ── Reducer ── */
function workspaceReducer(state, action) {
  switch (action.type) {
    case 'ADD_MESSAGE': {
      const msg = {
        id: `msg-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        timestamp: Date.now(),
        ...action.payload,
      };
      return {
        ...state,
        conversation: {
          ...state.conversation,
          messages: [...state.conversation.messages, msg],
        },
      };
    }

    case 'UPDATE_LAST_AI_MESSAGE': {
      const msgs = [...state.conversation.messages];
      // Find last AI message and append content
      for (let i = msgs.length - 1; i >= 0; i--) {
        if (msgs[i].sender === 'ai') {
          msgs[i] = { ...msgs[i], content: action.payload };
          break;
        }
      }
      return {
        ...state,
        conversation: { ...state.conversation, messages: msgs },
      };
    }

    case 'SET_INPUT_MODE':
      return {
        ...state,
        conversation: { ...state.conversation, inputMode: action.payload },
      };

    case 'SET_LISTENING':
      return {
        ...state,
        conversation: { ...state.conversation, isListening: action.payload },
      };

    case 'SET_PROCESSING':
      return {
        ...state,
        conversation: { ...state.conversation, isProcessing: action.payload },
      };

    case 'UPDATE_DOCUMENT_SECTION': {
      const { section, content, status } = action.payload;
      const newSections = {
        ...state.document.sections,
        [section]: { content, status: status || 'draft', updatedAt: Date.now() },
      };
      return {
        ...state,
        document: {
          ...state.document,
          sections: newSections,
          completionPercentage: calcCompletion(newSections),
        },
      };
    }

    case 'SET_DOCUMENT_TITLE':
      return {
        ...state,
        document: { ...state.document, title: action.payload },
      };

    case 'SET_ACTIVE_PANEL':
      return {
        ...state,
        ui: { ...state.ui, activePanel: action.payload },
      };

    case 'TOGGLE_DOCUMENT_SHEET':
      return {
        ...state,
        ui: { ...state.ui, documentSheetOpen: !state.ui.documentSheetOpen },
      };

    case 'SET_MOBILE':
      return {
        ...state,
        ui: { ...state.ui, isMobile: action.payload },
      };

    case 'SET_STARTED':
      return {
        ...state,
        ui: {
          ...state.ui,
          started: action.payload,
          hasStarted: action.payload ? true : state.ui.hasStarted,
        },
      };

    case 'START_SESSION':
      return {
        ...state,
        session: {
          sessionId: action.payload || `session-${Date.now()}`,
          startedAt: Date.now(),
        },
        ui: { ...state.ui, started: true, hasStarted: true },
      };

    case 'SET_CONCEPT_CONFIRMED':
      return {
        ...state,
        document: { ...state.document, userConfirmedConcept: true },
      };

    case 'HANDLE_CARD_RESPONSE': {
      const { cardId, response } = action.payload;
      return {
        ...state,
        conversation: {
          ...state.conversation,
          messages: state.conversation.messages.map(m =>
            m.cardData?.id === cardId
              ? { ...m, cardData: { ...m.cardData, answered: true, response } }
              : m
          ),
        },
      };
    }

    case 'RESTORE_SESSION': {
      const { document, session, messages } = action.payload;
      return {
        ...state,
        document: {
          ...document,
          completionPercentage: calcCompletion(document.sections),
          // Preserve userConfirmedConcept if previously saved; default false for
          // legacy sessions saved before this field existed.
          userConfirmedConcept: document.userConfirmedConcept === true,
        },
        session,
        conversation: {
          ...state.conversation,
          messages: messages || [],
        },
        ui: { ...state.ui, started: true, hasStarted: true },
      };
    }

    case 'RESET_SESSION':
      clearSavedSession();
      return { ...initialState };

    default:
      return state;
  }
}

/* ── Context ── */
const WorkspaceContext = createContext(null);

export function WorkspaceProvider({ children }) {
  const [state, dispatch] = useReducer(workspaceReducer, initialState);
  const saveTimerRef = useRef(null);

  // Auto-save to localStorage (debounced 500ms)
  useEffect(() => {
    // Only save if session has started and there's something worth saving
    if (!state.ui.hasStarted) return;
    clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => saveSession(state), 500);
    return () => clearTimeout(saveTimerRef.current);
  }, [state.document, state.conversation.messages, state.session]);

  // Convenience dispatchers
  const addMessage = useCallback((sender, content, cardData) => {
    dispatch({
      type: 'ADD_MESSAGE',
      payload: { sender, content, type: cardData ? 'card' : 'text', cardData },
    });
  }, []);

  const updateSection = useCallback((section, content, status) => {
    dispatch({
      type: 'UPDATE_DOCUMENT_SECTION',
      payload: { section, content, status },
    });
  }, []);

  return (
    <WorkspaceContext.Provider value={{ state, dispatch, addMessage, updateSection }}>
      {children}
    </WorkspaceContext.Provider>
  );
}

export function useWorkspace() {
  const ctx = useContext(WorkspaceContext);
  if (!ctx) throw new Error('useWorkspace must be used within WorkspaceProvider');
  return ctx;
}
